import React from 'react';
import { View, Text } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import {
  Inbox,
  Copy,
  Share2,
  Eye,
  X,
  RotateCcw,
  Link,
} from 'lucide-react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';

// ============================================================================
// Types
// ============================================================================

interface SubmissionEmptyStateProps {
  /** When true, the inbox has submissions but filters matched nothing */
  isFilterEmpty: boolean;
  /** When true, the ACTIVE view is empty only because every submission is in a
   *  terminal status (shortlisted/claimed/archived/rejected/invited) — the user
   *  set no search/filter. Distinguishes "you're all caught up" from "your
   *  filter matched nothing". */
  allProcessed?: boolean;
  /** Total number of submissions in the unfiltered inbox */
  totalSubmissions: number;
  /** Whether a search query is active */
  hasQuery: boolean;
  /** Number of active filters */
  activeFilterCount: number;
  onClearSearch: () => void;
  onResetFilters: () => void;
  onSetupLink: () => void;
  onCopyLink: () => void;
  onShareLink: () => void;
  hasSubmissionLink: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function SubmissionEmptyState({
  isFilterEmpty,
  allProcessed = false,
  totalSubmissions,
  hasQuery,
  activeFilterCount,
  onClearSearch,
  onResetFilters,
  onSetupLink,
  onCopyLink,
  onShareLink,
  hasSubmissionLink,
}: SubmissionEmptyStateProps) {
  const colors = useThemeColors();

  // State 2a: Active inbox is empty only because every submission is processed
  // (terminal status) — NOT because the user filtered. Explain the Total vs.
  // empty-list gap instead of telling them to "adjust filters" they never set.
  if (isFilterEmpty && allProcessed) {
    return (
      <Animated.View
        entering={FadeIn.delay(100).duration(300)}
        className="items-center py-8 px-4 w-full"
      >
        <View
          className="w-16 h-16 rounded-full items-center justify-center mb-3"
          style={{ backgroundColor: colors.traffic.greenLight }}
        >
          <Inbox size={28} color={colors.success} />
        </View>
        <Text style={{ color: colors.text.primary }} className="font-semibold text-center mb-1">
          You&apos;re all caught up
        </Text>
        <Text style={{ color: colors.text.tertiary }} className="text-center text-sm">
          {totalSubmissions === 1
            ? 'Your one submission has been processed'
            : `All ${totalSubmissions} submissions have been processed`}
          {' '}(shortlisted, archived, or rejected). Tap a status filter above to view them.
        </Text>
      </Animated.View>
    );
  }

  // State 2: Filters returned no results
  if (isFilterEmpty) {
    return (
      <Animated.View
        entering={FadeIn.delay(100).duration(300)}
        className="items-center py-8 px-4 w-full"
      >
        <View
          className="w-16 h-16 rounded-full items-center justify-center mb-3"
          style={{ backgroundColor: colors.surface.secondary }}
        >
          <Eye size={28} color={colors.text.tertiary} />
        </View>
        <Text style={{ color: colors.text.primary }} className="font-semibold text-center mb-1">
          No submissions match your filters
        </Text>
        <Text style={{ color: colors.text.tertiary }} className="text-center text-sm mb-4">
          Try adjusting your search or filters.
        </Text>

        <View className="flex-row flex-wrap justify-center gap-2">
          {hasQuery && (
            <AnimatedPressable
              onPress={onClearSearch}
              accessibilityRole="button"
              accessibilityLabel="Clear search"
              className="flex-row items-center gap-1.5 px-3 py-2 rounded-lg"
              style={{
                backgroundColor: colors.surface.primary,
                borderWidth: 1,
                borderColor: colors.border.light,
              }}
            >
              <X size={14} color={colors.text.secondary} />
              <Text style={{ color: colors.text.secondary }} className="text-sm">Clear search</Text>
            </AnimatedPressable>
          )}
          {activeFilterCount > 0 && (
            <AnimatedPressable
              onPress={onResetFilters}
              accessibilityRole="button"
              accessibilityLabel="Reset all filters"
              className="flex-row items-center gap-1.5 px-3 py-2 rounded-lg"
              style={{
                backgroundColor: colors.primary[50],
                borderWidth: 1,
                borderColor: colors.primary[200],
              }}
            >
              <RotateCcw size={14} color={colors.primary[600]} />
              <Text style={{ color: colors.primary[600] }} className="text-sm">Reset filters</Text>
            </AnimatedPressable>
          )}
        </View>

        <Text style={{ color: colors.text.tertiary }} className="text-xs mt-4">
          {totalSubmissions} submission{totalSubmissions !== 1 ? 's' : ''} in your inbox
        </Text>
      </Animated.View>
    );
  }

  // State 1: No submissions at all
  return (
    <Animated.View
      entering={FadeIn.delay(100).duration(400)}
      className="items-center py-8 px-4 w-full"
    >
      <View
        className="w-20 h-20 rounded-full items-center justify-center mb-4"
        style={{ backgroundColor: colors.primary[100] }}
      >
        <Inbox size={40} color={colors.primary[600]} />
      </View>
      <Text style={{ color: colors.text.primary }} className="text-xl font-bold text-center mb-2">
        No submissions yet
      </Text>
      <Text style={{ color: colors.text.secondary }} className="text-center mb-6 leading-5">
        Share your submission link to start receiving pitch decks.{'\n'}
        AI will score each deck automatically.
      </Text>

      {/* How it works */}
      <View
        className="w-full rounded-xl p-4 mb-6"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        <Text style={{ color: colors.text.primary }} className="font-semibold mb-3">
          How it works
        </Text>
        <View className="gap-3">
          {[
            { num: '1', title: 'Share your link', subtitle: 'Give startups your unique submission URL' },
            { num: '2', title: 'Receive pitch decks', subtitle: 'Startups upload their deck via your link' },
            { num: '3', title: 'AI scores automatically', subtitle: 'Get a 5-category score for each deck' },
          ].map((step) => (
            <View key={step.num} className="flex-row items-start gap-3">
              <View
                className="w-7 h-7 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.primary[100] }}
              >
                <Text style={{ color: colors.primary[600] }} className="font-bold text-sm">{step.num}</Text>
              </View>
              <View className="flex-1">
                <Text style={{ color: colors.text.primary }} className="font-medium text-sm">{step.title}</Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">{step.subtitle}</Text>
              </View>
            </View>
          ))}
        </View>
      </View>

      {/* CTAs */}
      {hasSubmissionLink ? (
        <View className="flex-row gap-3">
          <AnimatedPressable
            onPress={onCopyLink}
            accessibilityRole="button"
            accessibilityLabel="Copy your submission link"
            className="flex-row items-center gap-2 px-5 py-3.5 rounded-xl"
            style={{ backgroundColor: colors.primary[600] }}
          >
            <Copy size={18} color="white" />
            <Text className="text-white font-semibold">Copy Link</Text>
          </AnimatedPressable>
          <AnimatedPressable
            onPress={onShareLink}
            accessibilityRole="button"
            accessibilityLabel="Share your submission link"
            className="flex-row items-center gap-2 px-5 py-3.5 rounded-xl"
            style={{
              backgroundColor: colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.primary[200],
            }}
          >
            <Share2 size={18} color={colors.primary[600]} />
            <Text style={{ color: colors.primary[600] }} className="font-semibold">Share</Text>
          </AnimatedPressable>
        </View>
      ) : (
        <AnimatedPressable
          onPress={onSetupLink}
          accessibilityRole="button"
          accessibilityLabel="Set up your submission link"
          className="flex-row items-center gap-2 px-6 py-4 rounded-xl"
          style={{ backgroundColor: colors.primary[600] }}
        >
          <Link size={20} color="white" />
          <Text className="text-white font-semibold">Set Up Submission Link</Text>
        </AnimatedPressable>
      )}

      {/* Secondary action */}
      <AnimatedPressable
        onPress={onSetupLink}
        accessibilityRole="button"
        accessibilityLabel="Go to profile to manage submission link"
        className="flex-row items-center gap-2 mt-4 px-4 py-2"
      >
        <Text style={{ color: colors.text.secondary }} className="font-medium text-sm">
          {hasSubmissionLink ? 'Manage link in Profile' : 'Or: Go to Profile to set up your link'}
        </Text>
      </AnimatedPressable>
    </Animated.View>
  );
}
